import { diagnosticIconClass } from '../../styles/diagnostic-tooltip.sty.js';

type DiagnosticSeverity = 'error' | 'warning' | 'info';

/**
 * Severity icon glyphs, kept in sync with the `@dynatrace/strato-icons` sources
 * (`CriticalIcon`, `WarningIcon`, `InformationIcon`). Inlined as static SVG markup because the
 * tooltip content is built imperatively for CodeMirror rather than rendered through React.
 */
const ICON_PATHS: Record<DiagnosticSeverity, string> = {
  error:
    '<path d="M9.25 6.237h1.5v4.5h-1.5z" /><path d="M11 12.737a1 1 0 1 0-2 0 1 1 0 0 0 2 0" /><path d="M9.293 1.513a1 1 0 0 1 1.414 0l7.78 7.78a1 1 0 0 1 0 1.414l-7.78 7.78a1 1 0 0 1-1.414 0l-7.78-7.78a1 1 0 0 1 0-1.414zM17.073 10 10 2.927 2.927 10 10 17.073z" />',
  warning:
    '<path d="M9.372 7.913h1.507v4.54H9.372z" /><path d="M10.125 13.461A1.007 1.007 0 0 0 9.12 14.47a1.007 1.007 0 0 0 1.005 1.008 1.007 1.007 0 0 0 1.005-1.008 1.007 1.007 0 0 0-1.005-1.009" /><path d="M9.693 2.098c-.3.144-.527.573-.98 1.432L2.325 15.646c-.406.768-.608 1.153-.57 1.466a1 1 0 0 0 .424.708C2.437 18 2.87 18 3.736 18h12.778c.866 0 1.299 0 1.557-.18a1 1 0 0 0 .424-.708c.038-.313-.164-.698-.57-1.466L11.537 3.53c-.453-.859-.68-1.288-.98-1.432a1 1 0 0 0-.864 0m.432 1.989 6.539 12.4H3.586z" />',
  info: '<path d="M10.75 9v5h-1.5V9z" /><path d="M10 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2" /><path d="M10 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16m-6.5-8a6.5 6.5 0 1 0 13 0 6.5 6.5 0 0 0-13 0" />',
};

/**
 * Builds the content shown inside a CodeMirror lint tooltip: a leading severity icon followed
 * by the diagnostic message. Returned as a `Diagnostic.renderMessage` factory.
 *
 * @param severity - the diagnostic severity, driving which icon is shown.
 * @param message - the diagnostic message. By default applied via `textContent` (not `innerHTML`),
 * since messages can originate from consumers or external APIs and must not be treated as markup.
 * @param wrapperClass - CSS class applied to the outer flex container, letting each consumer
 * scope its own tooltip styling. The icon element always uses the shared {@link diagnosticIconClass}.
 * @param options - additional rendering options. `dangerouslyRenderHtml`, when `true`, injects the
 * message via `innerHTML` instead of `textContent` — only for **trusted, internally generated**
 * markup (e.g. FilterField's syntax-colored operator list), never for consumer- or API-provided
 * strings.
 * @internal
 */
export function renderDiagnosticMessage(
  severity: DiagnosticSeverity,
  message: string,
  wrapperClass: string,
  options?: { dangerouslyRenderHtml?: boolean },
): () => HTMLElement {
  return () => {
    const wrapper = document.createElement('div');
    wrapper.className = wrapperClass;

    const iconWrapper = document.createElement('div');
    iconWrapper.className = diagnosticIconClass;
    iconWrapper.innerHTML = `<svg focusable="false" role="img" aria-hidden="true" fill="currentColor" width="16" height="16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">${ICON_PATHS[severity]}</svg>`;

    const messageElement = document.createElement('div');
    if (options?.dangerouslyRenderHtml) {
      messageElement.innerHTML = message;
    } else {
      messageElement.textContent = message;
    }

    wrapper.append(iconWrapper, messageElement);
    return wrapper;
  };
}
